import { Link } from 'react-router';
import { KIND_LABELS, titleCase, yearOf } from '../lib/format.js';
import type { EntitySummary } from '../lib/types.js';
import { cx, MONO, TABLE_HEAD } from './classes.js';

/**
 * One search result (Results artboard, wireframe §4b): the registry and tax
 * records joined by name, with the join confidence shown as a number and a
 * bar — never hidden behind a single "verified" word. Ambiguous joins say
 * "Review n candidates" right here.
 */
export const RESULT_GRID =
  'grid grid-cols-[minmax(0,1fr)_120px_170px_110px] items-center gap-6 px-6';

export function ConfidenceBar({
  value,
  tone = 'ink',
}: {
  value: number;
  tone?: 'ink' | 'warn';
}) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <span className="flex flex-col gap-1.5">
      <span className={cx(MONO, 'text-[13px] text-ink')}>{value.toFixed(2)}</span>
      <span
        className="block h-1 overflow-hidden rounded-[2px] bg-sunk"
        aria-hidden="true"
      >
        <span
          className={cx(
            'block h-1',
            tone === 'warn' ? 'bg-warn' : 'bg-ink',
            widthClass(pct),
          )}
          data-width={pct}
        />
      </span>
    </span>
  );
}

/** Tailwind width utilities for the confidence bar, in 5% steps. */
const WIDTHS = [
  'w-0',
  'w-[5%]',
  'w-[10%]',
  'w-[15%]',
  'w-[20%]',
  'w-[25%]',
  'w-[30%]',
  'w-[35%]',
  'w-[40%]',
  'w-[45%]',
  'w-[50%]',
  'w-[55%]',
  'w-[60%]',
  'w-[65%]',
  'w-[70%]',
  'w-[75%]',
  'w-[80%]',
  'w-[85%]',
  'w-[90%]',
  'w-[95%]',
  'w-full',
] as const;

/** Width utility for the bar, in 5% steps (a class, never an inline width). */
export function widthClass(pct: number): string {
  const step = Math.round(pct / 5);
  return WIDTHS[step] ?? 'w-full';
}

export function registryStatusTone(status: string | null): string {
  const s = (status ?? '').trim().toLowerCase();
  if (s === 'active') return 'text-good';
  if (!s) return 'text-mute';
  return 'text-crit';
}

export interface EntityRowProps {
  entity: EntitySummary;
  /** Pending review candidates for this entity (0 = none). */
  reviewCount?: number;
  /** Register-specific meta line, e.g. "Company · reg. 1996 · Retail". */
  meta?: string;
  highlighted?: boolean;
  registrationDate?: string | null;
}

export function EntityRow({
  entity,
  reviewCount = 0,
  meta,
  highlighted = false,
  registrationDate,
}: EntityRowProps) {
  const year = yearOf(registrationDate ?? null);
  const metaLine =
    meta ??
    [KIND_LABELS[entity.kind], year ? `reg. ${year}` : null].filter(Boolean).join(' · ');
  const tax = entity.tax;
  const confidence = tax.confidence;
  const ambiguous = reviewCount > 0;
  // A business name is taxed under its owner's personal TPIN — its trading
  // name never appears in ZRA's search, so "no record" would be misleading:
  // the column says what is needed instead (owner fix 2026-09-22).
  const taxText = ambiguous
    ? `Review ${reviewCount} candidate${reviewCount === 1 ? '' : 's'}`
    : tax.linked
      ? tax.basis === 'owner_tpin'
        ? 'Owner TPIN linked'
        : 'TPIN linked'
      : tax.tpin
        ? `TPIN ${tax.tpin}`
        : entity.kind === 'business_name'
          ? 'Owner TPIN'
          : 'No tax record';
  return (
    <Link
      to={`/entities/${entity.id}`}
      className={cx(
        RESULT_GRID,
        'border-t border-line py-[18px] hover:bg-sunk/60',
        highlighted && 'bg-sunk',
      )}
      data-testid="entity-row"
    >
      <span className="flex flex-col gap-[3px]">
        <span className="text-[16px] font-semibold">{titleCase(entity.name)}</span>
        <span className="text-[13px] text-mute">{metaLine}</span>
      </span>
      <span className={cx('text-[14px] font-medium', registryStatusTone(entity.status))}>
        {entity.status ?? 'Unknown'}
      </span>
      <span
        className={cx(
          'text-[14px]',
          ambiguous ? 'text-warn' : tax.linked ? 'text-ink' : 'text-mute',
        )}
      >
        {taxText}
      </span>
      {typeof confidence === 'number' ? (
        <ConfidenceBar value={confidence} tone={ambiguous ? 'warn' : 'ink'} />
      ) : (
        <span className={cx(MONO, 'text-[13px] text-mute')}>—</span>
      )}
    </Link>
  );
}

export function EntityRowHead() {
  return (
    <div className={cx(RESULT_GRID, 'py-3', TABLE_HEAD)}>
      <span>ENTITY</span>
      <span>REGISTRY</span>
      <span>TAX RECORD</span>
      <span>MATCH</span>
    </div>
  );
}
